import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { employeesApi, schedulesApi, stationsApi, availabilityApi, scheduleNotesApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Users,
  Edit2,
  Trash2,
  Copy,
  ClipboardPaste,
  Calendar,
  CopyPlus,
  MoreHorizontal,
  Check,
  Loader2,
  CalendarDays,
  Settings,
  UserCheck,
  XCircle,
  RotateCcw,
  X,
  GripVertical,
  StickyNote,
  DollarSign,
  Save,
} from 'lucide-react';
import {
  format,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addWeeks,
  subWeeks,
  isSameDay,
  addDays,
  parseISO,
} from 'date-fns';
import { useTimeFormat } from '../utils/timeFormat';
import AvailabilityEditor from '../components/AvailabilityEditor';
import ShiftRequestsPanel from '../components/ShiftRequestsPanel';

const Schedule = () => {
  const { user, selectedLocation, locations } = useAuth();
  const fmtTime = useTimeFormat();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week'); // 'day', 'week', 'custom'
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stations, setStations] = useState([]);
  const [pendingClaims, setPendingClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddShift, setShowAddShift] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [editingShift, setEditingShift] = useState(null);
  const [copiedShift, setCopiedShift] = useState(null);
  const [showCopyWeekModal, setShowCopyWeekModal] = useState(false);
  const [showStationManager, setShowStationManager] = useState(false);
  const [showAvailabilityViewer, setShowAvailabilityViewer] = useState(false);
  const [availabilityList, setAvailabilityList] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [copyWeekTarget, setCopyWeekTarget] = useState(1);
  const [newStationName, setNewStationName] = useState('');
  const [editingStation, setEditingStation] = useState(null);
  const [newShift, setNewShift] = useState({
    employee_id: '',
    start_time: '09:00',
    end_time: '17:00',
    shift_name: 'Mid',
    break_start: '',
    break_end: '',
    notes: '',
    station: '',
    is_open_shift: false,
  });

  // Daily schedule notes — keyed by date string "YYYY-MM-DD"
  const [dayNotes, setDayNotes] = useState({});
  const [editingNoteDate, setEditingNoteDate] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Calculate view dates based on viewMode
  const getViewDates = () => {
    if (viewMode === 'day') {
      return { start: currentDate, end: currentDate };
    } else if (viewMode === 'custom' && customStartDate && customEndDate) {
      return { start: customStartDate, end: customEndDate };
    }
    // Default: week
    return { start: startOfWeek(currentDate, { weekStartsOn: 1 }), end: endOfWeek(currentDate, { weekStartsOn: 1 }) };
  };

  const { start: viewStart, end: viewEnd } = getViewDates();
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
  const displayDays = viewMode === 'day' 
    ? [currentDate]
    : viewMode === 'custom' && customStartDate && customEndDate
    ? eachDayOfInterval({ start: customStartDate, end: customEndDate })
    : eachDayOfInterval({ start: weekStart, end: weekEnd });
  const weekDays = displayDays;

  const canEditNotes = ['admin', 'accounting_admin', 'manager', 'assistant_manager'].includes(user?.role)
    && selectedLocation && selectedLocation !== 'all';
  const canSeeSummary = ['admin', 'accounting_admin', 'manager', 'assistant_manager'].includes(user?.role);

  // Per-day summary: hours, people count, est. wages (excluding the Location Manager)
  const dailySummaries = useMemo(() => {
    const map = {};
    const empById = new Map(employees.map((e) => [e.id, e]));
    const isLocationManager = (emp) => {
      if (!emp) return false;
      const pos = (emp.position || '').toLowerCase();
      // Exclude the Location Manager only — Assistant Manager is INCLUDED
      return pos === 'manager' || pos === 'location manager' || pos === 'general manager';
    };

    const computeHours = (start, end) => {
      if (!start || !end) return 0;
      const [sh, sm] = start.split(':').map(Number);
      const [eh, em] = end.split(':').map(Number);
      let diff = (eh * 60 + em) - (sh * 60 + sm);
      if (diff < 0) diff += 24 * 60; // overnight
      return diff / 60;
    };

    weekDays.forEach((day) => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const daySchedules = schedules.filter((s) => s.date === dateStr && !s.is_open_shift && s.employee_id);
      const uniqueEmps = new Set();
      let totalHours = 0;
      let totalWages = 0;
      daySchedules.forEach((s) => {
        const emp = empById.get(s.employee_id);
        const hours = computeHours(s.start_time, s.end_time);
        totalHours += hours;
        uniqueEmps.add(s.employee_id);
        if (emp && !isLocationManager(emp)) {
          totalWages += hours * (emp.hourly_rate || 0);
        }
      });
      map[dateStr] = {
        peopleCount: uniqueEmps.size,
        totalHours: Math.round(totalHours * 100) / 100,
        estimatedWages: Math.round(totalWages * 100) / 100,
      };
    });
    return map;
  }, [schedules, employees, weekDays]);

  useEffect(() => {
    loadData();
  }, [selectedLocation, currentDate, viewMode, customStartDate, customEndDate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [emps, scheds, stns] = await Promise.all([
        employeesApi.getAll(selectedLocation),
        schedulesApi.getAll({
          locationId: selectedLocation,
          startDate: format(viewStart, 'yyyy-MM-dd'),
          endDate: format(viewEnd, 'yyyy-MM-dd'),
        }),
        stationsApi.getAll(),
      ]);
      setEmployees(emps);
      setSchedules(scheds);
      setStations(stns);
      
      // Fetch pending claims for managers
      if (selectedLocation && selectedLocation !== 'all') {
        try {
          const claims = await schedulesApi.getPendingClaims(selectedLocation);
          setPendingClaims(claims);
        } catch (e) {
          setPendingClaims([]);
        }

        // Load daily schedule notes for this location/week
        try {
          const notes = await scheduleNotesApi.list({
            locationId: selectedLocation,
            startDate: format(viewStart, 'yyyy-MM-dd'),
            endDate: format(viewEnd, 'yyyy-MM-dd'),
          });
          const map = {};
          (notes || []).forEach((n) => { map[n.date] = n; });
          setDayNotes(map);
        } catch (e) {
          setDayNotes({});
        }
      } else {
        setDayNotes({});
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const startEditNote = (dateStr) => {
    setEditingNoteDate(dateStr);
    setEditingNoteText(dayNotes[dateStr]?.note || '');
  };

  const handleSaveNote = async () => {
    if (!editingNoteDate || !selectedLocation || selectedLocation === 'all') return;
    setSavingNote(true);
    try {
      const saved = await scheduleNotesApi.upsert({
        location_id: selectedLocation,
        date: editingNoteDate,
        note: editingNoteText.trim(),
      });
      const newMap = { ...dayNotes };
      if (saved?.deleted) {
        delete newMap[editingNoteDate];
      } else {
        newMap[editingNoteDate] = saved;
      }
      setDayNotes(newMap);
      setEditingNoteDate(null);
      setEditingNoteText('');
    } catch (e) {
      alert('Failed to save note: ' + e.message);
    } finally {
      setSavingNote(false);
    }
  };

  const getShiftColor = (shiftName) => {
    switch (shiftName) {
      case 'Morning': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Mid': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Evening': return 'bg-purple-100 text-purple-700 border-purple-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const handleAddShift = async () => {
    // For open shifts, employee_id will be '__OPEN__'
    if ((!newShift.employee_id && !newShift.is_open_shift) || !selectedDay) return;
    
    if (!newShift.station) {
      alert('Please select a station');
      return;
    }

    try {
      const isOpenShift = newShift.is_open_shift || newShift.employee_id === '__OPEN__';
      const scheduleData = {
        employee_id: isOpenShift ? '__OPEN__' : newShift.employee_id,
        location_id: selectedLocation !== 'all' ? selectedLocation : employees.find(e => e.id === newShift.employee_id)?.location_id,
        date: format(selectedDay, 'yyyy-MM-dd'),
        start_time: newShift.start_time,
        end_time: newShift.end_time,
        shift_name: newShift.shift_name,
        station: newShift.station,
        notes: newShift.notes,
        is_published: false, // New shifts are drafts by default
        is_open_shift: isOpenShift,
      };

      await schedulesApi.create(scheduleData);
      await loadData();
      setShowAddShift(false);
      setNewShift({ employee_id: '', start_time: '09:00', end_time: '17:00', shift_name: 'Mid', notes: '', station: '', is_open_shift: false });
    } catch (error) {
      alert('Failed to add shift: ' + error.message);
    }
  };

  const handleDeleteShift = async (shiftId) => {
    try {
      await schedulesApi.delete(shiftId);
      await loadData();
    } catch (error) {
      alert('Failed to delete shift: ' + error.message);
    }
  };

  const handleUpdateShift = async () => {
    if (!editingShift) return;

    try {
      await schedulesApi.update(editingShift.id, {
        start_time: editingShift.start_time,
        end_time: editingShift.end_time,
        shift_name: editingShift.shift_name,
        station: editingShift.station,
        notes: editingShift.notes,
      });
      await loadData();
      setEditingShift(null);
    } catch (error) {
      alert('Failed to update shift: ' + error.message);
    }
  };

  const handlePublishSchedule = async () => {
    if (selectedLocation === 'all') {
      alert('Please select a specific location to publish');
      return;
    }
    
    try {
      const result = await schedulesApi.publish(
        selectedLocation,
        format(viewStart, 'yyyy-MM-dd'),
        format(viewEnd, 'yyyy-MM-dd')
      );
      alert(result.message);
      await loadData();
    } catch (error) {
      alert('Failed to publish schedule: ' + error.message);
    }
  };

  const handleUnpublishSchedule = async () => {
    try {
      const result = await schedulesApi.unpublish(
        selectedLocation,
        format(viewStart, 'yyyy-MM-dd'),
        format(viewEnd, 'yyyy-MM-dd')
      );
      alert(result.message);
      await loadData();
    } catch (error) {
      alert('Failed to unpublish schedule: ' + error.message);
    }
  };

  const handleCopyShift = (shift) => {
    setCopiedShift(shift);
  };

  const handleCancelCopy = () => {
    setCopiedShift(null);
  };

  // Esc key exits copy mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && copiedShift) {
        setCopiedShift(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [copiedShift]);

  // Drag and drop
  const [draggedShift, setDraggedShift] = useState(null);

  const handleDragStart = (e, shift) => {
    setDraggedShift(shift);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', shift.id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, day, employeeId) => {
    e.preventDefault();
    if (!draggedShift) return;
    const newDate = format(day, 'yyyy-MM-dd');
    const isSameSlot = draggedShift.date === newDate && draggedShift.employee_id === employeeId;
    if (isSameSlot) {
      setDraggedShift(null);
      return;
    }
    try {
      await schedulesApi.update(draggedShift.id, {
        date: newDate,
        employee_id: employeeId,
      });
      await loadData();
    } catch (error) {
      alert('Failed to move shift: ' + error.message);
    }
    setDraggedShift(null);
  };

  const handleDragEnd = () => {
    setDraggedShift(null);
  };

  const handlePasteShift = async (day, employeeId) => {
    if (!copiedShift) return;

    try {
      await schedulesApi.create({
        employee_id: employeeId,
        location_id: copiedShift.location_id,
        date: format(day, 'yyyy-MM-dd'),
        start_time: copiedShift.start_time,
        end_time: copiedShift.end_time,
        shift_name: copiedShift.shift_name,
      });
      await loadData();
    } catch (error) {
      alert('Failed to paste shift: ' + error.message);
    }
  };

  const handleCopyWeek = async () => {
    try {
      const targetWeekStart = addWeeks(weekStart, copyWeekTarget);
      
      await schedulesApi.copyWeek({
        source_week_start: format(weekStart, 'yyyy-MM-dd'),
        target_week_start: format(targetWeekStart, 'yyyy-MM-dd'),
        location_id: selectedLocation !== 'all' ? selectedLocation : (locations || [])[0]?.id,
      });

      setShowCopyWeekModal(false);
      setCurrentDate(targetWeekStart);
    } catch (error) {
      alert('Failed to copy week: ' + error.message);
    }
  };

  const handleReviewClaim = async (claimId, status) => {
    try {
      await schedulesApi.reviewClaim(claimId, status);
      await loadData();
    } catch (error) {
      alert(`Failed to ${status} claim: ` + error.message);
    }
  };

  const handleAddStation = async () => {
    if (!newStationName.trim()) return;
    try {
      await stationsApi.create({ name: newStationName.trim(), description: '', is_active: true });
      setNewStationName('');
      const stns = await stationsApi.getAll();
      setStations(stns);
    } catch (error) {
      alert('Failed to add station: ' + error.message);
    }
  };

  const handleUpdateStation = async () => {
    if (!editingStation || !editingStation.name.trim()) return;
    try {
      await stationsApi.update(editingStation.id, { name: editingStation.name.trim() });
      setEditingStation(null);
      const stns = await stationsApi.getAll();
      setStations(stns);
    } catch (error) {
      alert('Failed to update station: ' + error.message);
    }
  };

  const handleDeleteStation = async (stationId) => {
    if (!window.confirm('Deactivate this station? Existing schedules won\'t be affected.')) return;
    try {
      await stationsApi.delete(stationId);
      const stns = await stationsApi.getAll();
      setStations(stns);
    } catch (error) {
      alert('Failed to deactivate station: ' + error.message);
    }
  };

  const handleReactivateStation = async (stationId) => {
    try {
      await stationsApi.reactivate(stationId);
      const stns = await stationsApi.getAll();
      setStations(stns);
    } catch (error) {
      alert('Failed to reactivate station: ' + error.message);
    }
  };

  const currentLocationName = selectedLocation === 'all' 
    ? 'All Locations' 
    : (locations || []).find((l) => l.id === selectedLocation)?.name;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-bold text-gray-900">Schedule</h1>
          <button onClick={loadData} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700" title="Refresh" data-testid="refresh-schedule-btn">
            <RotateCcw className="w-5 h-5" />
          </button>
        </div>
          <p className="text-gray-500 mt-1">
            Manage shifts for {currentLocationName}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {copiedShift && (
            <Badge variant="outline" className="gap-2 py-2 pr-1 cursor-pointer" data-testid="copy-mode-indicator">
              <ClipboardPaste className="w-4 h-4" />
              Shift copied
              <button
                onClick={handleCancelCopy}
                className="ml-1 p-1 rounded-full hover:bg-gray-200 transition-colors"
                title="Cancel copy mode (Esc)"
                data-testid="cancel-copy-btn"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          {/* Publish Status Indicator */}
          {schedules.length > 0 && (
            <Badge 
              className={schedules.some(s => !s.is_published) ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}
            >
              {schedules.every(s => s.is_published) ? 'Published' : schedules.some(s => s.is_published) ? 'Partially Published' : 'Draft'}
            </Badge>
          )}
          {['admin', 'accounting_admin', 'manager', 'assistant_manager'].includes(user?.role) && (
            <Button
              variant="outline"
              onClick={async () => {
                setShowAvailabilityViewer(true);
                setAvailabilityLoading(true);
                try {
                  const list = await availabilityApi.list(selectedLocation);
                  setAvailabilityList(list);
                } catch (e) {
                  alert('Failed to load availability: ' + e.message);
                } finally {
                  setAvailabilityLoading(false);
                }
              }}
              data-testid="view-availability-btn"
            >
              <CalendarDays className="w-4 h-4 mr-2" />
              Availability
            </Button>
          )}
          {['admin', 'accounting_admin', 'manager'].includes(user?.role) && (
            <Button variant="outline" onClick={() => setShowStationManager(true)} data-testid="manage-stations-btn">
              <Settings className="w-4 h-4 mr-2" />
              Stations
            </Button>
          )}
          <Button variant="outline" onClick={() => setShowCopyWeekModal(true)}>
            <CopyPlus className="w-4 h-4 mr-2" />
            Copy Week
          </Button>
          {/* Publish Button */}
          {selectedLocation !== 'all' && schedules.some(s => !s.is_published) && (
            <Button 
              onClick={handlePublishSchedule} 
              className="bg-green-600 hover:bg-green-700"
              data-testid="publish-schedule-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Publish Schedule
            </Button>
          )}
          {selectedLocation !== 'all' && schedules.every(s => s.is_published) && schedules.length > 0 && (
            <Button 
              variant="outline"
              onClick={handleUnpublishSchedule}
              data-testid="unpublish-schedule-btn"
            >
              Unpublish
            </Button>
          )}
        </div>
      </div>

      {/* Week Navigation */}
      <Card className="mb-6 border-0 shadow-md">
        <CardContent className="p-4">
          {/* View Mode Selector */}
          <div className="flex items-center justify-center gap-2 mb-4">
            <Button 
              variant={viewMode === 'day' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setViewMode('day')}
              className={viewMode === 'day' ? 'bg-purple-600 hover:bg-purple-700' : ''}
            >
              Day
            </Button>
            <Button 
              variant={viewMode === 'week' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setViewMode('week')}
              className={viewMode === 'week' ? 'bg-purple-600 hover:bg-purple-700' : ''}
            >
              Week
            </Button>
            <Button 
              variant={viewMode === 'custom' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => {
                setViewMode('custom');
                if (!customStartDate) setCustomStartDate(currentDate);
                if (!customEndDate) setCustomEndDate(addDays(currentDate, 6));
              }}
              className={viewMode === 'custom' ? 'bg-purple-600 hover:bg-purple-700' : ''}
            >
              Custom Range
            </Button>
          </div>
          
          {/* Custom Date Range Picker */}
          {viewMode === 'custom' && (
            <div className="flex items-center justify-center gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Label className="text-sm text-gray-600">From:</Label>
                <Input
                  type="date"
                  value={customStartDate ? format(customStartDate, 'yyyy-MM-dd') : ''}
                  onChange={(e) => setCustomStartDate(e.target.value ? parseISO(e.target.value) : null)}
                  className="w-40"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-gray-600">To:</Label>
                <Input
                  type="date"
                  value={customEndDate ? format(customEndDate, 'yyyy-MM-dd') : ''}
                  onChange={(e) => setCustomEndDate(e.target.value ? parseISO(e.target.value) : null)}
                  className="w-40"
                />
              </div>
            </div>
          )}
          
          {/* Navigation Arrows */}
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => {
              if (viewMode === 'day') {
                setCurrentDate(addDays(currentDate, -1));
              } else {
                setCurrentDate(subWeeks(currentDate, 1));
              }
            }}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="text-center flex items-center gap-3">
              <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="gap-2">
                    <CalendarDays className="w-4 h-4" />
                    {viewMode === 'day' 
                      ? format(currentDate, 'EEEE, MMMM d, yyyy')
                      : viewMode === 'custom' && customStartDate && customEndDate
                      ? `${format(customStartDate, 'MMM d')} - ${format(customEndDate, 'MMM d, yyyy')}`
                      : `${format(weekStart, 'MMMM d')} - ${format(weekEnd, 'MMMM d, yyyy')}`
                    }
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="center">
                  <CalendarComponent
                    mode="single"
                    selected={currentDate}
                    onSelect={(date) => {
                      if (date) {
                        setCurrentDate(date);
                        setShowDatePicker(false);
                      }
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <Button variant="link" onClick={() => setCurrentDate(new Date())} className="text-purple-600">
                Go to Today
              </Button>
            </div>
            <Button variant="ghost" onClick={() => {
              if (viewMode === 'day') {
                setCurrentDate(addDays(currentDate, 1));
              } else {
                setCurrentDate(addWeeks(currentDate, 1));
              }
            }}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Schedule Grid */}
      <Card className="border-0 shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 w-48">Employee</th>
                {weekDays.map((day) => {
                  const isToday = isSameDay(day, new Date());
                  return (
                    <th key={day.toISOString()} className={`px-2 py-3 text-center text-sm font-medium min-w-[140px] ${isToday ? 'bg-purple-50' : ''}`}>
                      <div className={isToday ? 'text-purple-600' : 'text-gray-500'}>
                        {format(day, 'EEE')}
                        <span className={`block text-lg ${isToday ? 'font-bold' : 'font-semibold text-gray-900'}`}>
                          {format(day, 'd')}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {/* Daily Notes Row */}
              <tr className="border-b border-gray-100 bg-yellow-50/60">
                <td className="px-4 py-2 align-top">
                  <div className="flex items-center gap-2 text-xs font-medium text-yellow-700">
                    <StickyNote className="w-3.5 h-3.5" />
                    Day Notes
                  </div>
                </td>
                {weekDays.map((day) => {
                  const dateStr = format(day, 'yyyy-MM-dd');
                  const noteObj = dayNotes[dateStr];
                  const isEditing = editingNoteDate === dateStr;
                  return (
                    <td key={day.toISOString()} className="px-2 py-2 align-top text-xs" data-testid={`day-notes-cell-${dateStr}`}>
                      {isEditing ? (
                        <div className="space-y-1">
                          <textarea
                            className="w-full text-xs p-1.5 border border-yellow-300 rounded resize-none focus:outline-none focus:ring-2 focus:ring-yellow-400"
                            rows={3}
                            value={editingNoteText}
                            onChange={(e) => setEditingNoteText(e.target.value)}
                            placeholder="Notes for this day…"
                            data-testid={`day-notes-input-${dateStr}`}
                            autoFocus
                          />
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => { setEditingNoteDate(null); setEditingNoteText(''); }} disabled={savingNote}>
                              Cancel
                            </Button>
                            <Button size="sm" className="h-6 px-2 text-xs bg-yellow-600 hover:bg-yellow-700" onClick={handleSaveNote} disabled={savingNote} data-testid={`day-notes-save-${dateStr}`}>
                              {savingNote ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className={`w-full text-left p-1.5 rounded ${noteObj ? 'bg-yellow-100 text-yellow-900' : 'text-yellow-600 italic hover:bg-yellow-100'} ${canEditNotes ? 'cursor-pointer' : 'cursor-default'}`}
                          onClick={() => canEditNotes && startEditNote(dateStr)}
                          disabled={!canEditNotes}
                          data-testid={`day-notes-display-${dateStr}`}
                          title={noteObj?.note || (canEditNotes ? 'Click to add a note' : 'No note')}
                        >
                          {noteObj?.note ? (
                            <span className="block whitespace-pre-wrap line-clamp-3">{noteObj.note}</span>
                          ) : (
                            <span className="text-[10px]">{canEditNotes ? '+ Add note' : '—'}</span>
                          )}
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
              {employees.map((emp) => (
                <tr key={emp.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={emp.avatar} alt={emp.name} />
                        <AvatarFallback className="bg-gray-200 text-xs">
                          {emp.name?.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{emp.name}</p>
                        <p className="text-xs text-gray-500">{emp.position}</p>
                      </div>
                    </div>
                  </td>
                  {weekDays.map((day) => {
                    const daySchedules = schedules.filter(
                      (s) => s.employee_id === emp.id && s.date === format(day, 'yyyy-MM-dd')
                    );
                    const isToday = isSameDay(day, new Date());

                    return (
                      <td key={day.toISOString()} className={`px-2 py-2 ${isToday ? 'bg-purple-50/50' : ''} ${draggedShift ? 'transition-colors' : ''}`}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, day, emp.id)}
                      >
                        <div className="space-y-1">
                          {daySchedules.map((shift) => (
                            <div
                              key={shift.id}
                              draggable
                              onDragStart={(e) => handleDragStart(e, shift)}
                              onDragEnd={handleDragEnd}
                              className={`text-xs p-2 rounded-lg border cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow relative group ${getShiftColor(shift.shift_name)} ${draggedShift?.id === shift.id ? 'opacity-40' : ''}`}
                              data-testid={`shift-card-${shift.id}`}
                            >
                              <p className="font-medium">{fmtTime.local(shift.start_time)} - {fmtTime.local(shift.end_time)}</p>
                              <p className="opacity-75">{shift.shift_name}</p>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button
                                    className="absolute top-1 right-1 p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-black/10 transition-opacity"
                                    onClick={(e) => e.stopPropagation()}
                                    onPointerDown={(e) => e.stopPropagation()}
                                    data-testid={`shift-menu-${shift.id}`}
                                  >
                                    <MoreHorizontal className="w-3.5 h-3.5" />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent>
                                  <DropdownMenuItem onClick={() => setEditingShift(shift)}>
                                    <Edit2 className="w-4 h-4 mr-2" /> Edit Shift
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleCopyShift(shift)}>
                                    <Copy className="w-4 h-4 mr-2" /> Copy Shift
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => handleDeleteShift(shift.id)} className="text-red-600">
                                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          ))}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full h-8 text-gray-400 hover:text-purple-600 hover:bg-purple-50"
                            onClick={() => copiedShift ? handlePasteShift(day, emp.id) : (setSelectedDay(day), setNewShift({ ...newShift, employee_id: emp.id }), setShowAddShift(true))}
                          >
                            {copiedShift ? <ClipboardPaste className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                          </Button>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {/* Open Shifts Row */}
              {schedules.some(s => s.is_open_shift) && (
                <tr className="border-b border-gray-100 bg-green-50/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                        <Users className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-green-700 text-sm">Open Shifts</p>
                        <p className="text-xs text-green-500">Unassigned</p>
                      </div>
                    </div>
                  </td>
                  {weekDays.map((day) => {
                    const dayOpenShifts = schedules.filter(
                      (s) => s.is_open_shift && s.date === format(day, 'yyyy-MM-dd')
                    );
                    const isToday = isSameDay(day, new Date());
                    return (
                      <td key={day.toISOString()} className={`px-2 py-2 ${isToday ? 'bg-purple-50/50' : ''}`}>
                        <div className="space-y-1">
                          {dayOpenShifts.map((shift) => (
                            <DropdownMenu key={shift.id}>
                              <DropdownMenuTrigger asChild>
                                <div className="text-xs p-2 rounded-lg border cursor-pointer hover:shadow-md transition-shadow bg-green-100 text-green-700 border-green-200" data-testid={`open-shift-${shift.id}`}>
                                  <p className="font-medium">{fmtTime.local(shift.start_time)} - {fmtTime.local(shift.end_time)}</p>
                                  <p className="opacity-75">{shift.station || 'Open'}</p>
                                  {!shift.is_published && <Badge className="mt-1 bg-amber-100 text-amber-700 text-[10px]">Draft</Badge>}
                                </div>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                <DropdownMenuItem onClick={() => setEditingShift(shift)}>
                                  <Edit2 className="w-4 h-4 mr-2" /> Edit
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleDeleteShift(shift.id)} className="text-red-600">
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ))}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              )}
              {/* Daily Summary Row */}
              {canSeeSummary && (
                <tr className="border-t-2 border-purple-300 bg-purple-50/70" data-testid="schedule-daily-summary-row">
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-center gap-2 text-xs font-semibold text-purple-800">
                      <DollarSign className="w-3.5 h-3.5" />
                      Daily Summary
                    </div>
                  </td>
                  {weekDays.map((day) => {
                    const dateStr = format(day, 'yyyy-MM-dd');
                    const s = dailySummaries[dateStr] || { peopleCount: 0, totalHours: 0, estimatedWages: 0 };
                    return (
                      <td key={day.toISOString()} className="px-2 py-3 align-top text-xs" data-testid={`schedule-daily-summary-${dateStr}`}>
                        <div className="space-y-0.5">
                          <div className="flex items-center justify-between">
                            <span className="text-purple-700">People</span>
                            <span className="font-semibold text-gray-900">{s.peopleCount}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-purple-700">Hours</span>
                            <span className="font-semibold text-gray-900">{s.totalHours.toFixed(1)}</span>
                          </div>
                          <div className="flex items-center justify-between border-t border-purple-200 pt-0.5 mt-0.5">
                            <span className="text-purple-700">Est. wages</span>
                            <span className="font-bold text-purple-900">${s.estimatedWages.toFixed(2)}</span>
                          </div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {employees.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No employees found for this location</p>
          </div>
        )}
      </Card>

      {/* Pending Shift Claims */}
      {pendingClaims.length > 0 && (
        <Card className="mt-6 border-0 shadow-md" data-testid="pending-claims-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <UserCheck className="w-5 h-5 text-amber-600" />
              Pending Shift Claims ({pendingClaims.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingClaims.map((claim) => (
                <div key={claim.id} className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div>
                    <p className="font-medium text-gray-900">{claim.employee_name}</p>
                    <p className="text-sm text-gray-600">
                      Wants to claim shift on <span className="font-medium">{claim.schedule?.date}</span>
                      {claim.schedule?.start_time && ` (${claim.schedule.start_time} - ${claim.schedule.end_time})`}
                      {claim.schedule?.station && ` at ${claim.schedule.station}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleReviewClaim(claim.id, 'approved')}
                      data-testid={`approve-claim-${claim.id}`}
                    >
                      <Check className="w-4 h-4 mr-1" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => handleReviewClaim(claim.id, 'rejected')}
                      data-testid={`reject-claim-${claim.id}`}
                    >
                      <XCircle className="w-4 h-4 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Shift Modal */}
      <Dialog open={showAddShift} onOpenChange={setShowAddShift}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Shift for {selectedDay && format(selectedDay, 'EEEE, MMMM d')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Employee</Label>
              <Select value={newShift.employee_id} onValueChange={(v) => setNewShift({ ...newShift, employee_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time</Label>
                <Input type="time" value={newShift.start_time} onChange={(e) => setNewShift({ ...newShift, start_time: e.target.value })} />
              </div>
              <div>
                <Label>End Time</Label>
                <Input type="time" value={newShift.end_time} onChange={(e) => setNewShift({ ...newShift, end_time: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Shift Type</Label>
              <Select value={newShift.shift_name} onValueChange={(v) => setNewShift({ ...newShift, shift_name: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Morning">Morning (6am-2pm)</SelectItem>
                  <SelectItem value="Mid">Mid (10am-6pm)</SelectItem>
                  <SelectItem value="Evening">Evening (2pm-10pm)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Station/Position <span className="text-red-500">*</span></Label>
              <Select value={newShift.station} onValueChange={(v) => setNewShift({ ...newShift, station: v })}>
                <SelectTrigger data-testid="shift-station-select"><SelectValue placeholder="Select station" /></SelectTrigger>
                <SelectContent>
                  {stations.map((station) => (
                    <SelectItem key={station.id} value={station.name}>{station.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_open_shift"
                checked={newShift.is_open_shift}
                onChange={(e) => setNewShift({ ...newShift, is_open_shift: e.target.checked, employee_id: e.target.checked ? '__OPEN__' : '' })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="is_open_shift" className="cursor-pointer">Create as Open Shift (unassigned)</Label>
            </div>
            <div>
              <Label>Notes (Optional)</Label>
              <Input 
                placeholder="Add any instructions or notes..." 
                value={newShift.notes} 
                onChange={(e) => setNewShift({ ...newShift, notes: e.target.value })}
                data-testid="shift-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddShift(false)}>Cancel</Button>
            <Button onClick={handleAddShift} className="bg-purple-600 hover:bg-purple-700" disabled={!newShift.employee_id || !newShift.station}>Add Shift</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Shift Modal */}
      <Dialog open={!!editingShift} onOpenChange={() => setEditingShift(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Shift</DialogTitle>
          </DialogHeader>
          {editingShift && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Time</Label>
                  <Input type="time" value={editingShift.start_time} onChange={(e) => setEditingShift({ ...editingShift, start_time: e.target.value })} />
                </div>
                <div>
                  <Label>End Time</Label>
                  <Input type="time" value={editingShift.end_time} onChange={(e) => setEditingShift({ ...editingShift, end_time: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Shift Type</Label>
                <Select value={editingShift.shift_name} onValueChange={(v) => setEditingShift({ ...editingShift, shift_name: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Morning">Morning</SelectItem>
                    <SelectItem value="Mid">Mid</SelectItem>
                    <SelectItem value="Evening">Evening</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Station/Position</Label>
                <Select value={editingShift.station || ''} onValueChange={(v) => setEditingShift({ ...editingShift, station: v })}>
                  <SelectTrigger><SelectValue placeholder="Select station" /></SelectTrigger>
                  <SelectContent>
                    {stations.map((station) => (
                      <SelectItem key={station.id} value={station.name}>{station.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes</Label>
                <Input 
                  placeholder="Add any instructions or notes..." 
                  value={editingShift.notes || ''} 
                  onChange={(e) => setEditingShift({ ...editingShift, notes: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingShift(null)}>Cancel</Button>
            <Button onClick={handleUpdateShift} className="bg-purple-600 hover:bg-purple-700">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy Week Modal */}
      <Dialog open={showCopyWeekModal} onOpenChange={setShowCopyWeekModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CopyPlus className="w-5 h-5" />Copy Week Schedule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Copy all shifts from:</p>
              <p className="font-semibold">{format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}</p>
            </div>
            <div>
              <Label>Copy to week</Label>
              <Select value={copyWeekTarget.toString()} onValueChange={(v) => setCopyWeekTarget(parseInt(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4].map((weeks) => {
                    const target = addWeeks(weekStart, weeks);
                    return (
                      <SelectItem key={weeks} value={weeks.toString()}>
                        {format(target, 'MMM d')} - {format(addDays(target, 6), 'MMM d, yyyy')}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCopyWeekModal(false)}>Cancel</Button>
            <Button onClick={handleCopyWeek} className="bg-purple-600 hover:bg-purple-700">
              <Copy className="w-4 h-4 mr-2" />Copy Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Station Management Modal */}
      <Dialog open={showStationManager} onOpenChange={setShowStationManager}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Manage Stations / Positions
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Add new station */}
            <div className="flex gap-2">
              <Input
                placeholder="New station name..."
                value={newStationName}
                onChange={(e) => setNewStationName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddStation()}
                data-testid="new-station-input"
              />
              <Button onClick={handleAddStation} className="bg-purple-600 hover:bg-purple-700" disabled={!newStationName.trim()} data-testid="add-station-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>

            {/* Station list */}
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {stations.map((station) => (
                <div key={station.id} className="flex items-center justify-between p-3 rounded-lg border bg-white" data-testid={`station-row-${station.id}`}>
                  {editingStation?.id === station.id ? (
                    <div className="flex gap-2 flex-1 mr-2">
                      <Input
                        value={editingStation.name}
                        onChange={(e) => setEditingStation({ ...editingStation, name: e.target.value })}
                        onKeyPress={(e) => e.key === 'Enter' && handleUpdateStation()}
                        autoFocus
                        data-testid="edit-station-input"
                      />
                      <Button size="sm" onClick={handleUpdateStation} className="bg-green-600 hover:bg-green-700">
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingStation(null)}>
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div>
                        <span className="font-medium text-gray-900">{station.name}</span>
                        {station.description && <span className="text-sm text-gray-500 ml-2">{station.description}</span>}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditingStation({ id: station.id, name: station.name })} data-testid={`edit-station-${station.id}`}>
                          <Edit2 className="w-4 h-4 text-gray-500" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteStation(station.id)} data-testid={`delete-station-${station.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400">Deactivating a station won&apos;t affect existing schedules.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStationManager(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee Availability Viewer */}
      <Dialog open={showAvailabilityViewer} onOpenChange={setShowAvailabilityViewer}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="availability-viewer-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-purple-600" />
              Employee Availability
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-xs text-gray-500 mb-4">
              Use this view to check each employee&apos;s availability while building or editing the schedule.
            </p>
            {availabilityLoading ? (
              <div className="text-center py-8"><RotateCcw className="w-6 h-6 mx-auto animate-spin text-purple-600" /></div>
            ) : availabilityList.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No employees in this location yet.</div>
            ) : (
              <div className="space-y-4">
                {availabilityList.map((row) => (
                  <details key={row.employee_id} className="border rounded-lg overflow-hidden">
                    <summary className="cursor-pointer p-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between" data-testid={`availability-row-${row.employee_id}`}>
                      <div>
                        <span className="font-medium text-gray-900">{row.name}</span>
                        <span className="text-xs text-gray-500 ml-2">{row.employee_number || '—'} · {row.position || 'Employee'}</span>
                      </div>
                      <span className="text-xs text-gray-400">
                        {row.updated_at ? `Updated ${format(parseISO(row.updated_at), 'MMM d, yyyy')}` : 'Not yet submitted'}
                      </span>
                    </summary>
                    <div className="p-3 bg-white">
                      <AvailabilityEditor value={{ days: row.days, notes: row.notes }} onChange={() => {}} readOnly />
                    </div>
                  </details>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAvailabilityViewer(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shift Trade & Cover Requests Queue */}
      {['admin', 'accounting_admin', 'manager', 'assistant_manager'].includes(user?.role) && (
        <ShiftRequestsPanel locationId={selectedLocation} onReviewed={loadData} />
      )}
    </div>
  );
};

export default Schedule;
